// pages/teams/TeamDetailsPage.jsx
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import DetailsTab from "./tabs/DetailsTab";
import PlayersTab from "./tabs/PlayersTab";
import TournamentsTab from "./tabs/TournamentsTab";
import LoadingSpinner from "../../utils/general/LoadingSpinner";
import { FaUsers } from "react-icons/fa";
import { HiPlus } from "react-icons/hi";
import { showToast } from "../../components/ToastNotification";
import CategoryPopup from "../../utils/tournament/categoryPopup";
import { ArrowLeft, Pencil, Check } from "lucide-react";
import { jwtDecode } from "jwt-decode";

const TeamDetailsPage = () => {
  const token = useSelector((state) => state.user.token);
  const { id } = useParams();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("details");
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [editCategoriesMode, setEditCategoriesMode] = useState(false);
  const [showAddCategoryPopup, setShowAddCategoryPopup] = useState(false);
  const [userId, setUserId] = useState("");
  const isTeamOwner = userId && team?.user_id ? userId === team.user_id : false;

  const sortSubcategories = (subcategories) => {
    return [...subcategories].sort((a, b) => {
      const aHasNumber = a.category && a.category !== 0;
      const bHasNumber = b.category && b.category !== 0;

      if (aHasNumber && bHasNumber) {
        if (a.category !== b.category) return a.category - b.category;
        return (a.group_name || "").localeCompare(b.group_name || "");
      }
      if (aHasNumber && !bHasNumber) return -1;
      if (!aHasNumber && bHasNumber) return 1;
      return (a.group_name || "").localeCompare(b.group_name || "");
    });
  };

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.id);
      } catch (err) {
        console.error("Token inválido", err);
      }
    }
  }, [token]);

  const sortedCategories = useMemo(() => {
    if (!team?.categories) return [];
    return sortSubcategories(team.categories);
  }, [team]);

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teams/${id}/categories/${categoryId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error("Error al eliminar");

      // Recargar datos del equipo
      const updated = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teams/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await updated.json();
      setTeam(data);

      if (selectedSubcategory?.id === categoryId) {
        setSelectedSubcategory(data.categories?.[0] || null);
      }
      showToast.success("Categoría eliminada");
    } catch (error) {
      console.error(error);
      showToast.error("No se pudo eliminar la categoría");
    }
  };

  const handleAddCategories = async (newCategories) => {
    // newCategories es un array de objetos { number, group_name }
    try {
      for (const cat of newCategories) {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/teams/${id}/categories`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              category: cat.number ? parseInt(cat.number) : 0,
              group_name: cat.group_name || null,
            }),
          },
        );
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.message || "Error al agregar");
        }
      }

      // Recargar el equipo para tener las categorías actualizadas
      const updated = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teams/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await updated.json();
      setTeam(data);
      setShowAddCategoryPopup(false);
      showToast.success("Categoría(s) añadida(s)");
    } catch (error) {
      console.error(error);
      showToast.error("No se pudo agregar la(s) categoría(s)");
    }
  };

  useEffect(() => {
    const fetchTeamDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/teams/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok)
          throw new Error("Error al obtener detalles del equipo.");

        const data = await response.json();
        setTeam(data);

        if (data.categories?.length > 0) {
          setSelectedSubcategory(sortSubcategories(data.categories)[0]);
        }
      } catch (error) {
        setError("Error al obtener detalles del equipo.");
        console.error(error);
      }
    };

    fetchTeamDetails();
  }, [id, token]);

  const formatSubcategoryDisplay = (subcategory) => {
    if (!subcategory.category || subcategory.category === 0) {
      return subcategory.group_name || "Sin categoría";
    }
    return subcategory.group_name
      ? `${subcategory.category} ${subcategory.group_name}`
      : subcategory.category.toString();
  };

  if (error) return <p className="text-red-500">{error}</p>;
  if (!team) return <LoadingSpinner />;

  return (
    <div className="bg-gray-50 min-h-screen font-sans p-4">
      <div className="max-w-2xl mx-auto p-4">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          body { font-family: 'Inter', sans-serif; }
        `}</style>

        {/* Header del equipo */}
        <div className="flex items-center gap-4 p-4 rounded-3xl bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 p-2 rounded-full transition"
          >
            <ArrowLeft size={22} strokeWidth={2.5} />
          </button>
          {team.picture_url ? (
            <img
              src={team.picture_url}
              alt={team.team_name}
              className="w-16 h-16 rounded-xl object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
              <FaUsers className="text-white" size={28} />
            </div>
          )}

          <div>
            <h1 className="text-xl font-bold">{team.team_name}</h1>
            <p className="text-sm opacity-90">Equipo</p>
          </div>
        </div>

        {/* Subcategorías (siempre visible para permitir agregar) */}
        <div className="overflow-x-auto mb-4">
          <div className="flex items-center gap-3">
            {/* Botón icono para activar/desactivar edición */}
            {isTeamOwner && (
              <button
                onClick={() => setEditCategoriesMode(!editCategoriesMode)}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors flex-shrink-0"
                title={
                  editCategoriesMode ? "Terminar edición" : "Editar categorías"
                }
              >
                {editCategoriesMode ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <Pencil size={18} className="text-gray-600" />
                )}
              </button>
            )}

            {sortedCategories.length > 0 ? (
              <>
                {sortedCategories.map((subcategory) => (
                  <div key={subcategory.id} className="relative flex-shrink-0">
                    <button
                      onClick={() => setSelectedSubcategory(subcategory)}
                      className={`px-4 py-2 rounded-2xl whitespace-nowrap font-medium transition-colors ${
                        selectedSubcategory?.id === subcategory.id
                          ? "bg-blue-500 text-white shadow-md"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {formatSubcategoryDisplay(subcategory)}
                    </button>
                    {editCategoriesMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(subcategory.id);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                {editCategoriesMode && (
                  <button
                    onClick={() => setShowAddCategoryPopup(true)}
                    className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 flex items-center justify-center flex-shrink-0"
                    title="Agregar categoría"
                  >
                    <HiPlus size={16} />
                  </button>
                )}
              </>
            ) : (
              // Si no hay categorías, mostramos mensaje y posibilidad de añadir
              <div className="text-gray-500 text-sm whitespace-nowrap">
                No hay categorías asignadas.
                {editCategoriesMode && (
                  <button
                    onClick={() => setShowAddCategoryPopup(true)}
                    className="ml-2 p-1 rounded-full bg-green-500 text-white hover:bg-green-600 inline-flex items-center justify-center"
                    title="Agregar categoría"
                  >
                    <HiPlus size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-around bg-white rounded-2xl shadow-md p-2 mb-4">
          {["details", "players", "tournaments"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                activeTab === tab
                  ? "bg-blue-500 text-white shadow"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {tab === "details" && "Detalles"}
              {tab === "players" && "Jugadores"}
              {tab === "tournaments" && "Torneos"}
            </button>
          ))}
        </div>

        {/* Contenido del tab */}
        <div className="bg-white rounded-3xl shadow p-4">
          {activeTab === "details" && (
            <DetailsTab team={team} selectedSubcategory={selectedSubcategory} />
          )}
          {activeTab === "players" && (
            <PlayersTab team={team} selectedSubcategory={selectedSubcategory} />
          )}
          {activeTab === "tournaments" && (
            <TournamentsTab
              team={team}
              selectedSubcategory={selectedSubcategory}
            />
          )}
        </div>

        {/* Popup para añadir categorías */}
        {showAddCategoryPopup && (
          <CategoryPopup
            onClose={() => setShowAddCategoryPopup(false)}
            onSave={handleAddCategories} // ahora espera y procesa todo el array
            initialSubcategories={[]}
          />
        )}
      </div>
    </div>
  );
};

export default TeamDetailsPage;
